import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import router, {editFeaturedProduct, newFeaturedProductPart, Route} from "@/routing/Router";
import AdminLayout from "@widgets/layout/AdminLayout";
import {useQuery} from "@tanstack/react-query";
import {RestaurantLocation} from "@/data/entities/restaurant";
import {SetStateFn} from "@/utils/SetStateUtils";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import FeaturedProductEditor from "@widgets/featuredProducts/editor/FeaturedProductEditor";
import {api} from "@/data/api";
import {FeaturedProductDetails} from "@/data/entities/product";

const getOptionalFeaturedProduct = async (
    featuredProductId: string | null | undefined,
    dashboardLocation: RestaurantLocation,
): Promise<FeaturedProductDetails | null> => {
    if (featuredProductId === undefined || featuredProductId === null || featuredProductId === newFeaturedProductPart) {
        return null;
    }

    return api.featuredProduct.getFeaturedProductDetails(dashboardLocation.id, parseInt(featuredProductId));
};

export type SetFeaturedProductProps = {
    featuredProduct: FeaturedProductDetails | null,
    setFeaturedProduct: SetStateFn<FeaturedProductDetails | null>,
};

const FeaturedProduct = () => {
    const {featuredProductId} = useParams();
    const [featuredProduct, setFeaturedProduct] = useState<FeaturedProductDetails | null>(null);
    const {dashboardLocation} = useRestaurant();
    const {data, isLoading, error} = useQuery({
        queryKey: ["featured-product", featuredProductId || "new", dashboardLocation],
        queryFn: () => getOptionalFeaturedProduct(featuredProductId, dashboardLocation),
    });
    const route: Route = useMemo(
        () => featuredProduct ? editFeaturedProduct(featuredProduct.id ?? -1) : router.newFeaturedProduct,
        [featuredProduct]);

    // Place fetched entity in state
    useEffect(() => {
        if (data !== undefined && data !== null) {
            setFeaturedProduct(data);
        }
    }, [data]);

    // Trigger error page to show
    if (error) {
        throw new Error("Cannot load featured product");
    }

    return (
        <AdminLayout currentPage={route}>
            <FeaturedProductEditor
                key={featuredProduct?.id || "new"}
                route={route}
                featuredProduct={featuredProduct}
                setFeaturedProduct={setFeaturedProduct}
                initialLoading={isLoading}
                restaurantId={dashboardLocation.id}
            />
        </AdminLayout>
    );
};

export default FeaturedProduct;